import { z } from "zod";

const positiveInteger = z.number().int().min(1).max(2_147_483_647);
const taskId = z.string().uuid();
const sha256 = z.string().regex(/^[0-9a-f]{64}$/);
const revision = z.string().trim().min(1).max(255);
const revisionId = z.string().uuid();
const storageKey = z.string().trim().min(1).max(512);

export const AI_TASK_FEATURES = [
  "resume-review",
  "resume-upload",
  "career-analysis",
  "resume-spellcheck",
  "interview-questions",
  "interview-evaluation",
  "cover-letter-prompts",
  "cover-letter-audio",
  "company-recommendation",
  "job-posting-search",
  "job-posting-import",
] as const;

export const aiTaskFeatureSchema = z.enum(AI_TASK_FEATURES);
export type AiTaskFeatureRuntime = z.infer<typeof aiTaskFeatureSchema>;

export const AI_TASK_STATUSES = [
  "queued",
  "running",
  "succeeded",
  "failed",
  "cancelled",
] as const;

export const aiTaskStatusSchema = z.enum(AI_TASK_STATUSES);
export type AiTaskStatusRuntime = z.infer<typeof aiTaskStatusSchema>;

export const AI_TASK_AD_POLICIES = [
  "none",
  "banner",
  "interstitial-eligible",
] as const;

const jsonValueSchema: z.ZodType<
  string | number | boolean | null | Array<unknown> | Record<string, unknown>
> = z.lazy(() =>
  z.union([
    z.string(),
    z.number().finite(),
    z.boolean(),
    z.null(),
    z.array(jsonValueSchema),
    z.record(z.string(), jsonValueSchema),
  ]),
);

/** The DB JSON boundary. Feature contracts below are deliberately stricter. */
export const aiTaskJsonObjectSchema = z.record(z.string(), jsonValueSchema);
export type AiTaskJsonObject = z.infer<typeof aiTaskJsonObjectSchema>;

const resumeReviewInputSchema = z
  .object({
    analysisId: positiveInteger,
    resumeId: positiveInteger,
    companyId: positiveInteger,
    resumeRevisionId: revisionId.optional(),
    allowTicket: z.boolean().optional(),
  })
  .strict();
const resumeReviewResultSchema = z
  .object({ analysisId: positiveInteger })
  .strict();

const resumeUploadInputSchema = z
  .object({
    storageKey,
    checksum: sha256,
    mimeType: z.string().trim().min(1).max(128),
    size: z.number().int().min(1).max(100 * 1024 * 1024),
    originalFilename: z.string().trim().min(1).max(255),
    title: z.string().trim().min(1).max(255).optional(),
  })
  .strict();
const resumeUploadResultSchema = z
  .object({ resumeId: positiveInteger })
  .strict();

const careerAnalysisInputSchema = z
  .object({
    resumeId: positiveInteger,
    resumeRevision: revision,
    resumeRevisionId: revisionId.optional(),
    resumeHash: sha256.optional(),
    notify: z.boolean().optional(),
  })
  .strict();
const careerAnalysisSchema = z
  .object({
    strengths: z.array(z.object({
      title: z.string().max(1000),
      evidence: z.string().max(1000),
    }).strict()).max(5),
    ncsCategories: z.array(z.object({
      major: z.string().max(1000),
      middle: z.string().max(1000),
      level: z.number().int().min(0).max(8).nullable(),
      fitReason: z.string().max(1000),
    }).strict()).max(3),
    recommendedJobs: z.array(z.object({
      job: z.string().max(1000),
      ncsCategory: z.string().max(1000),
      fitScore: z.number().int().min(0).max(100).nullable(),
      reason: z.string().max(1000),
      outlook: z.string().max(1000),
      outlookScore: z.number().int().min(0).max(100).nullable(),
      salaryGuide: z.object({
        min: z.number().int().min(0).max(100000).nullable(),
        max: z.number().int().min(0).max(100000).nullable(),
        basis: z.string().max(1000),
      }).strict(),
    }).strict()).max(3),
    summary: z.string().max(20000),
    resumeSummary: z.string().max(1000),
    salaryGuide: z.object({
      min: z.number().int().min(0).max(100000).nullable(),
      max: z.number().int().min(0).max(100000).nullable(),
      basis: z.string().max(1000),
    }).strict(),
    typoIssues: z.array(z.object({
      found: z.string().max(300),
      suggestion: z.string().max(300),
      note: z.string().max(300),
    }).strict()).max(10),
    dateIssues: z.array(z.object({
      found: z.string().max(300),
      issue: z.string().max(300),
      suggestion: z.string().max(300),
    }).strict()).max(10),
    papers: z.array(z.object({
      title: z.string().max(1000),
      authors: z.array(z.string().max(300)).max(30),
      applicantAuthorOrder: z.number().int().min(0).max(30).nullable(),
      venue: z.string().max(1000),
      abstract: z.string().max(5000),
      issues: z.array(z.string().max(1000)).max(10),
      verification: z.object({
        status: z.enum(["verified", "unverified", "conflict"]),
        records: z.array(z.object({
          source: z.enum(["Crossref", "OpenAlex", "KCI"]),
          title: z.string().max(1000),
          authors: z.array(z.string().max(300)).max(30),
          venue: z.string().max(1000),
          doi: z.string().max(500),
          url: z.string().max(2048),
        }).strict()).max(3),
        conflicts: z.array(z.string().max(1000)).max(10),
      }).strict(),
    }).strict()).max(10),
  })
  .strict();
const careerAnalysisResultSchema = z
  .object({
    resumeId: positiveInteger,
    resumeRevision: revision,
    resumeRevisionId: revisionId.optional(),
    analysis: careerAnalysisSchema.optional(),
  })
  .strict();

const resumeSpellcheckInputSchema = z
  .object({
    resumeId: positiveInteger,
    resumeRevision: revision,
    resumeRevisionId: revisionId.optional(),
    contentHash: sha256,
  })
  .strict();
const spellcheckCorrectionSchema = z
  .object({
    field: z.enum(["content", "coverLetter"]),
    original: z.string().max(10_000),
    corrected: z.string().max(10_000),
    reason: z.string().max(1_000),
  })
  .strict();
const resumeSpellcheckResultSchema = z
  .object({
    resumeId: positiveInteger,
    resumeRevision: revision,
    resumeRevisionId: revisionId.optional(),
    corrections: z.array(spellcheckCorrectionSchema).max(10_000),
    contentCorrected: z.string().max(500_000),
    coverLetterCorrected: z.string().max(500_000),
  })
  .strict();

const interviewQuestionsInputSchema = z
  .object({
    interviewId: positiveInteger,
    resumeId: positiveInteger,
    companyId: positiveInteger,
    resumeRevisionId: revisionId.optional(),
  })
  .strict();
const interviewQuestionsResultSchema = z
  .object({
    interviewId: positiveInteger,
    questionCount: z.number().int().min(0).max(100),
  })
  .strict();

const interviewEvaluationInputSchema = z
  .object({
    interviewId: positiveInteger,
    answersRevision: revision,
  })
  .strict();
const interviewEvaluationResultSchema = z
  .object({
    interviewId: positiveInteger,
    score: z.number().finite().min(0).max(100).nullable(),
  })
  .strict();

const coverLetterPromptsInputSchema = z
  .object({
    resumeId: positiveInteger,
    resumeRevision: revision,
    resumeRevisionId: revisionId.optional(),
  })
  .strict();
const coverLetterPromptsResultSchema = z
  .object({
    resumeId: positiveInteger,
    resumeRevision: revision,
    resumeRevisionId: revisionId.optional(),
    prompts: z.array(z.string().trim().min(1).max(5_000)).min(1).max(20),
    usedFallback: z.boolean(),
  })
  .strict();

const coverLetterAudioInputSchema = z
  .object({
    storageKey,
    checksum: sha256,
    mimeType: z.string().trim().min(1).max(128),
    size: z.number().int().min(1).max(50 * 1024 * 1024),
    originalFilename: z.string().trim().min(1).max(255),
  })
  .strict();
const coverLetterAudioResultSchema = z
  .object({
    transcript: z.string().max(500_000),
    coverLetter: z.string().max(500_000),
    resumeId: positiveInteger.nullable(),
  })
  .strict();

const companyRecommendationInputSchema = z
  .object({
    resumeId: positiveInteger,
    resumeRevision: revision,
    resumeRevisionId: revisionId.optional(),
    resumeHash: sha256.optional(),
    companyIds: z.array(positiveInteger).min(1).max(1_000),
    companySnapshot: z
      .array(
        z
          .object({
            id: positiveInteger,
            name: z.string().trim().min(1).max(255),
            updatedAt: revision,
            jobDescription: z.string().max(10_000).nullable(),
            talentProfile: z.string().max(5_000).nullable(),
          })
          .strict(),
      )
      .min(1)
      .max(1_000)
      .optional(),
  })
  .strict();
const companyRecommendationResultSchema = z
  .object({
    companyIds: z.array(positiveInteger).min(1).max(1_000),
    recommendations: z
      .array(
        z
          .object({
            companyId: positiveInteger,
            name: z.string().trim().min(1).max(255),
            rationale: z.string().max(5_000),
          })
          .strict(),
      )
      .max(1_000),
  })
  .strict();

const jobPostingSearchInputSchema = z
  .object({
    resumeId: positiveInteger.nullable(),
    resumeRevision: revision.optional(),
    resumeRevisionId: revisionId.optional(),
    filters: z
      .object({
        careerLevel: z.array(z.string().trim().min(1).max(100)).max(20),
        educationLevel: z.string().trim().max(100).nullable(),
        recruitmentField: z
          .array(z.string().trim().min(1).max(200))
          .max(20),
      })
      .strict(),
    searchRequestId: taskId,
  })
  .strict();
const jobPostingSearchResultSchema = z
  .object({
    searchId: taskId,
    batchId: taskId,
    version: z.number().int().min(0).max(2_147_483_647),
    blocked: z.boolean(),
    providerStatus: z.array(z.object({
      provider: z.enum(["saramin", "jobkorea", "wanted"]),
      exhausted: z.boolean(),
      failure: z.enum(["blocked", "rate_limited", "timeout", "upstream_error", "parse_error", "unsupported_filters", "not_configured"]).nullable(),
      retryAt: z.string().nullable(),
      fetchedPages: z.number().int().min(0),
      pending: z.number().int().min(0),
    }).strict()).max(3),
    completion: z.enum(["more", "exhausted", "interrupted"]),
    isLast: z.boolean(),
    retryAfterMs: z.number().int().min(0).nullable(),
    warnings: z.array(z.string().max(500)).max(50),
    postings: z
      .array(
        z
          .object({
            candidateKey: z.string().trim().min(1).max(512),
            externalId: z.string().trim().min(1).max(255),
            title: z.string().trim().min(1).max(500),
            companyName: z.string().trim().min(1).max(255),
            url: z.string().url().max(2_048),
            imported: z.boolean(),
            locationText: z.string().max(500).nullable(),
            careerText: z.string().max(500).nullable(),
            educationText: z.string().max(500).nullable(),
            summary: z.string().max(10_000).nullable(),
            jobDescription: z.string().max(200_000).nullable(),
            fetchedAt: z.string().trim().min(1).max(255),
            detailStatus: z.enum(["not_requested", "ok", "blocked", "unavailable", "empty"]),
          })
          .strict(),
      )
      .max(2_000),
  })
  .strict();

const jobPostingImportInputSchema = z
  .object({
    companyId: positiveInteger.nullable(),
    normalizedUrl: z.string().url().max(2_048),
  })
  .strict();
const jobPostingImportResultSchema = z
  .object({
    companyId: positiveInteger.nullable(),
    title: z.string().max(500),
    text: z.string().max(1_000_000),
    source: z.enum(["html", "ocr"]),
    normalizedUrl: z.string().url().max(2_048),
  })
  .strict();

const analysisTargetSchema = z
  .object({ type: z.literal("analysis"), analysisId: positiveInteger })
  .strict();
const resumeCareerTargetSchema = z
  .object({ type: z.literal("resume-career"), resumeId: positiveInteger })
  .strict();
const resumeSpellcheckTargetSchema = z
  .object({
    type: z.literal("resume-spellcheck"),
    resumeId: positiveInteger,
    taskId,
  })
  .strict();
const resumeUploadTargetSchema = z
  .object({
    type: z.literal("resume-upload"),
    resumeId: positiveInteger,
    taskId,
  })
  .strict();
const interviewTargetSchema = z
  .object({ type: z.literal("interview"), interviewId: positiveInteger })
  .strict();
const coverLetterTargetSchema = z
  .object({ type: z.literal("cover-letter"), taskId })
  .strict();
const companiesTargetSchema = z
  .object({ type: z.literal("companies"), taskId })
  .strict();
const companyDraftTargetSchema = z
  .object({ type: z.literal("company-draft"), taskId })
  .strict();
const companyTargetSchema = z
  .object({ type: z.literal("company"), companyId: positiveInteger, taskId })
  .strict();

export const aiResultTargetSchema = z.discriminatedUnion("type", [
  analysisTargetSchema,
  resumeCareerTargetSchema,
  resumeSpellcheckTargetSchema,
  resumeUploadTargetSchema,
  interviewTargetSchema,
  coverLetterTargetSchema,
  companiesTargetSchema,
  companyDraftTargetSchema,
  companyTargetSchema,
]);
export type AiResultTargetRuntime = z.infer<typeof aiResultTargetSchema>;

export const aiTaskInputSchemas = {
  "resume-review": resumeReviewInputSchema,
  "resume-upload": resumeUploadInputSchema,
  "career-analysis": careerAnalysisInputSchema,
  "resume-spellcheck": resumeSpellcheckInputSchema,
  "interview-questions": interviewQuestionsInputSchema,
  "interview-evaluation": interviewEvaluationInputSchema,
  "cover-letter-prompts": coverLetterPromptsInputSchema,
  "cover-letter-audio": coverLetterAudioInputSchema,
  "company-recommendation": companyRecommendationInputSchema,
  "job-posting-search": jobPostingSearchInputSchema,
  "job-posting-import": jobPostingImportInputSchema,
} as const;

export const aiTaskResultSchemas = {
  "resume-review": resumeReviewResultSchema,
  "resume-upload": resumeUploadResultSchema,
  "career-analysis": careerAnalysisResultSchema,
  "resume-spellcheck": resumeSpellcheckResultSchema,
  "interview-questions": interviewQuestionsResultSchema,
  "interview-evaluation": interviewEvaluationResultSchema,
  "cover-letter-prompts": coverLetterPromptsResultSchema,
  "cover-letter-audio": coverLetterAudioResultSchema,
  "company-recommendation": companyRecommendationResultSchema,
  "job-posting-search": jobPostingSearchResultSchema,
  "job-posting-import": jobPostingImportResultSchema,
} as const;

export type AiTaskFeatureContract = {
  inputSchema: z.ZodType;
  resultSchema: z.ZodType;
  targetSchema: z.ZodType;
  executorKey: AiTaskFeatureRuntime;
  adPolicy: (typeof AI_TASK_AD_POLICIES)[number];
  successResultRequired: boolean;
  retentionPolicyKey: string;
  buildResultTarget: (
    result: Record<string, unknown>,
    context: { taskId: string },
  ) => AiResultTargetRuntime;
};

export const aiTaskFeatureRegistry: Record<
  AiTaskFeatureRuntime,
  AiTaskFeatureContract
> = {
  "resume-review": {
    inputSchema: resumeReviewInputSchema,
    resultSchema: resumeReviewResultSchema,
    targetSchema: analysisTargetSchema,
    executorKey: "resume-review",
    adPolicy: "none",
    successResultRequired: true,
    retentionPolicyKey: "analysis-projection",
    buildResultTarget: (result) => ({
      type: "analysis",
      analysisId: positiveInteger.parse(result.analysisId),
    }),
  },
  "resume-upload": {
    inputSchema: resumeUploadInputSchema,
    resultSchema: resumeUploadResultSchema,
    targetSchema: resumeUploadTargetSchema,
    executorKey: "resume-upload",
    adPolicy: "banner",
    successResultRequired: true,
    retentionPolicyKey: "resume-upload-reference",
    buildResultTarget: (result, context) => ({
      type: "resume-upload",
      resumeId: positiveInteger.parse(result.resumeId),
      taskId: taskId.parse(context.taskId),
    }),
  },
  "career-analysis": {
    inputSchema: careerAnalysisInputSchema,
    resultSchema: careerAnalysisResultSchema,
    targetSchema: resumeCareerTargetSchema,
    executorKey: "career-analysis",
    adPolicy: "banner",
    successResultRequired: true,
    retentionPolicyKey: "resume-career-projection",
    buildResultTarget: (result) => ({
      type: "resume-career",
      resumeId: positiveInteger.parse(result.resumeId),
    }),
  },
  "resume-spellcheck": {
    inputSchema: resumeSpellcheckInputSchema,
    resultSchema: resumeSpellcheckResultSchema,
    targetSchema: resumeSpellcheckTargetSchema,
    executorKey: "resume-spellcheck",
    adPolicy: "banner",
    successResultRequired: true,
    retentionPolicyKey: "resume-spellcheck-projection",
    buildResultTarget: (result, context) => ({
      type: "resume-spellcheck",
      resumeId: positiveInteger.parse(result.resumeId),
      taskId: taskId.parse(context.taskId),
    }),
  },
  "interview-questions": {
    inputSchema: interviewQuestionsInputSchema,
    resultSchema: interviewQuestionsResultSchema,
    targetSchema: interviewTargetSchema,
    executorKey: "interview-questions",
    adPolicy: "interstitial-eligible",
    successResultRequired: true,
    retentionPolicyKey: "interview-session",
    buildResultTarget: (result) => ({
      type: "interview",
      interviewId: positiveInteger.parse(result.interviewId),
    }),
  },
  "interview-evaluation": {
    inputSchema: interviewEvaluationInputSchema,
    resultSchema: interviewEvaluationResultSchema,
    targetSchema: interviewTargetSchema,
    executorKey: "interview-evaluation",
    adPolicy: "banner",
    successResultRequired: true,
    retentionPolicyKey: "interview-session",
    buildResultTarget: (result) => ({
      type: "interview",
      interviewId: positiveInteger.parse(result.interviewId),
    }),
  },
  "cover-letter-prompts": {
    inputSchema: coverLetterPromptsInputSchema,
    resultSchema: coverLetterPromptsResultSchema,
    targetSchema: coverLetterTargetSchema,
    executorKey: "cover-letter-prompts",
    adPolicy: "banner",
    successResultRequired: true,
    retentionPolicyKey: "cover-letter-projection",
    buildResultTarget: (_result, context) => ({
      type: "cover-letter",
      taskId: taskId.parse(context.taskId),
    }),
  },
  "cover-letter-audio": {
    inputSchema: coverLetterAudioInputSchema,
    resultSchema: coverLetterAudioResultSchema,
    targetSchema: coverLetterTargetSchema,
    executorKey: "cover-letter-audio",
    adPolicy: "banner",
    successResultRequired: true,
    retentionPolicyKey: "cover-letter-audio-projection",
    buildResultTarget: (_result, context) => ({
      type: "cover-letter",
      taskId: taskId.parse(context.taskId),
    }),
  },
  "company-recommendation": {
    inputSchema: companyRecommendationInputSchema,
    resultSchema: companyRecommendationResultSchema,
    targetSchema: companiesTargetSchema,
    executorKey: "company-recommendation",
    adPolicy: "banner",
    successResultRequired: true,
    retentionPolicyKey: "company-recommendation-projection",
    buildResultTarget: (_result, context) => ({
      type: "companies",
      taskId: taskId.parse(context.taskId),
    }),
  },
  "job-posting-search": {
    inputSchema: jobPostingSearchInputSchema,
    resultSchema: jobPostingSearchResultSchema,
    targetSchema: companiesTargetSchema,
    executorKey: "job-posting-search",
    adPolicy: "banner",
    successResultRequired: true,
    retentionPolicyKey: "job-posting-search-projection",
    buildResultTarget: (_result, context) => ({
      type: "companies",
      taskId: taskId.parse(context.taskId),
    }),
  },
  "job-posting-import": {
    inputSchema: jobPostingImportInputSchema,
    resultSchema: jobPostingImportResultSchema,
    targetSchema: z.union([companyTargetSchema, companyDraftTargetSchema]),
    executorKey: "job-posting-import",
    adPolicy: "banner",
    successResultRequired: true,
    retentionPolicyKey: "job-posting-import-projection",
    buildResultTarget: (result, context) => result.companyId === null
      ? { type: "company-draft", taskId: taskId.parse(context.taskId) }
      : {
          type: "company",
          companyId: positiveInteger.parse(result.companyId),
          taskId: taskId.parse(context.taskId),
        },
  },
};

export function getAiTaskFeatureContract(
  feature: AiTaskFeatureRuntime,
): AiTaskFeatureContract {
  return aiTaskFeatureRegistry[feature];
}

export function parseAiTaskInput(
  feature: AiTaskFeatureRuntime,
  input: unknown,
): AiTaskJsonObject {
  return getAiTaskFeatureContract(feature).inputSchema.parse(input) as AiTaskJsonObject;
}

export function parseAiTaskResult(
  feature: AiTaskFeatureRuntime,
  result: unknown,
): AiTaskJsonObject {
  return getAiTaskFeatureContract(feature).resultSchema.parse(result) as AiTaskJsonObject;
}

export function parseAiTaskResultTarget(
  feature: AiTaskFeatureRuntime,
  target: unknown,
): AiResultTargetRuntime {
  return getAiTaskFeatureContract(feature).targetSchema.parse(target);
}

const publicResultEnvelopes = AI_TASK_FEATURES.map((feature) =>
  z
    .object({
      feature: z.literal(feature),
      result: aiTaskResultSchemas[feature],
    })
    .strict(),
) as unknown as [
  z.ZodDiscriminatedUnionOption<"feature">,
  ...z.ZodDiscriminatedUnionOption<"feature">[],
];

export const aiTaskPublicResultSchema = z.discriminatedUnion(
  "feature",
  publicResultEnvelopes,
);

const isoTimestamp = z.string().datetime({ offset: true });
export const AI_TASK_ERROR_CODES = [
  "IDEMPOTENCY_CONFLICT",
  "AI_TASK_ACTIVE_LIMIT",
  "INVALID_AI_TASK_CURSOR",
  "AI_TASK_NOT_FOUND",
  "AI_TASK_NOT_TERMINAL",
  "AI_TASK_INVALID_INPUT",
  "AI_TASK_INVALID_RESULT",
  "AI_TASK_INVALID_TARGET",
  "AI_TASK_INVALID_ID",
] as const;
export const aiTaskErrorCodeSchema = z.enum(AI_TASK_ERROR_CODES);
export const aiTaskFailureCodeSchema = z.string().trim().min(1).max(255);

export const idempotencyKeySchema = z.string().trim().min(1).max(255);

export const aiTaskSummarySchema = z
  .object({
    id: taskId,
    feature: aiTaskFeatureSchema,
    status: aiTaskStatusSchema,
    target: aiResultTargetSchema.nullable(),
    createdAt: isoTimestamp,
    startedAt: isoTimestamp.nullable(),
    completedAt: isoTimestamp.nullable(),
    viewedAt: isoTimestamp.nullable(),
    errorCode: aiTaskFailureCodeSchema.nullable(),
    errorMessage: z.string().max(2_000).nullable(),
  })
  .strict();

export const aiTaskDetailSchema = aiTaskSummarySchema
  .extend({ result: aiTaskPublicResultSchema.nullable() })
  .strict();

export const aiTaskListResponseSchema = z
  .object({
    items: z.array(aiTaskSummarySchema),
    nextCursor: z.string().min(1).max(1_024).nullable(),
  })
  .strict();

export const aiTaskViewedResponseSchema = z
  .object({ id: taskId, viewedAt: isoTimestamp })
  .strict();

export const aiTaskListQuerySchema = z
  .object({
    cursor: z.string().min(1).max(1_024).optional(),
    limit: z.coerce.number().int().min(1).max(50).default(20),
    feature: aiTaskFeatureSchema.optional(),
    status: aiTaskStatusSchema.optional(),
  })
  .strict();

export type AiTaskSummaryRuntime = z.infer<typeof aiTaskSummarySchema>;
export type AiTaskDetailRuntime = z.infer<typeof aiTaskDetailSchema>;
export type AiTaskListResponseRuntime = z.infer<typeof aiTaskListResponseSchema>;
